"use client";

import React, { useMemo, useCallback, useState } from "react";
import { format } from "date-fns";
import { AnimatePresence } from "framer-motion";
import { useAppRouter } from "@/src/shared/hooks/useAppRouter";
import {
  type IFilter,
  BookingDateModalMobile,
  BookingGuestModalMobile,
  WhereModalMobile,
  MobileSearchModal,
  searchService,
  type MobileSearchStep,
} from "@/src/shared/features/search";
import BackButton from "@/src/shared/components/atoms/BackButton";
import { noBackButtonPathsForMobileDevice } from "@/src/shared/constants/app";
import { useLockBodyScroll } from "@/src/shared/hooks/useLockBodyScroll";

type Section = "main" | "where" | "when" | "who";

interface IMobileHeaderProps {
  filters: IFilter;
  getTotalGuests: () => string;
  updateGuestCount: (
    type: keyof IFilter["guests"],
    op: "add" | "subtract",
  ) => void;
  setFilters: React.Dispatch<React.SetStateAction<IFilter>>;
  isSearchActive: boolean;
  setIsSearchActive: React.Dispatch<React.SetStateAction<boolean>>;
  isMobile: boolean;
  closeSearch: () => void;
  handleSearch: (next?: IFilter) => void;
}

const MobileHeader: React.FC<IMobileHeaderProps> = ({
  filters,
  getTotalGuests,
  updateGuestCount,
  setFilters,
  isSearchActive,
  setIsSearchActive,
  isMobile,
  closeSearch,
  handleSearch,
}) => {
  const router = useAppRouter();
  const [mobileSearchSection, setMobileSearchSection] =
    useState<Section>("main");
  const [isGoingBack, setIsGoingBack] = useState(false);
  const [isGuided, setIsGuided] = useState(false);

  useLockBodyScroll(isMobile && isSearchActive);

  const showBackButton = useMemo(
    () => !noBackButtonPathsForMobileDevice.includes(router.asPath),
    [router.asPath],
  );

  const activateMobileSearch = useCallback(() => {
    const start = searchService.mobileSearchStart(filters);
    setIsGuided(start !== null);
    setIsGoingBack(false);
    setMobileSearchSection(start ?? "main");
    setIsSearchActive(true);
  }, [filters, setIsSearchActive]);

  const goToStep = useCallback((step: MobileSearchStep) => {
    setIsGoingBack(false);
    setMobileSearchSection(step);
  }, []);

  const leaveGuidedSearch = useCallback(() => {
    setIsGuided(false);
    setMobileSearchSection("main");
    closeSearch();
  }, [closeSearch]);

  const finishGuidedSearch = useCallback(
    (guests: IFilter["guests"]) => {
      setIsGuided(false);
      handleSearch({ ...filters, guests });
    },
    [filters, handleSearch],
  );

  const commitDates = useCallback(
    (dates: { checkIn: Date | null; checkOut: Date | null }) =>
      setFilters((f) => ({ ...f, ...dates })),
    [setFilters],
  );

  const commitGuests = useCallback(
    (guests: IFilter["guests"]) => setFilters((f) => ({ ...f, guests })),
    [setFilters],
  );

  const handleMobileSectionSelect = useCallback(
    (section: Exclude<Section, "main">) => {
      setMobileSearchSection(section);
      setIsGoingBack(false);
    },
    [],
  );

  const handleMobileBack = useCallback(() => {
    setIsGoingBack(true);
    const timer = setTimeout(() => {
      setMobileSearchSection("main");
      setIsGoingBack(false);
    }, 400);
    return () => clearTimeout(timer);
  }, []);

  const handleBack = useCallback(() => {
    router.back();
  }, [router]);

  const destinationLabel = useMemo(() => {
    const name = filters.destination?.name;
    if (!name) return "Location";
    return name.length > 20 ? `${name.slice(0, 20)}...` : name;
  }, [filters.destination?.name]);

  const dateGuestsLabel = useMemo(() => {
    if (filters.checkIn && filters.checkOut) {
      return `${format(filters.checkIn, "d MMM")} – ${format(
        filters.checkOut,
        "d MMM",
      )} • ${getTotalGuests()}`;
    }
    return "Dates • Guests";
  }, [filters.checkIn, filters.checkOut, getTotalGuests]);

  const hasAnyFilter = Boolean(
    filters.destination?.name || (filters.checkIn && filters.checkOut),
  );

  return (
    <>
      <div className="md:hidden px-4 pt-3 pb-1 !bg-generic-white">
        {hasAnyFilter ? (
          <div className="flex items-center gap-3">
            {showBackButton && <BackButton onClick={handleBack} />}

            <button
              onClick={activateMobileSearch}
              className="flex-1 bg-white rounded-full shadow-md border border-gray-200 px-4 py-2"
              aria-label="Open search"
              type="button"
            >
              <div className="text-center">
                <div className="text-sm font-medium text-ink-black leading-tight capitalize">
                  {destinationLabel}
                </div>
                <div className="text-xs text-ink-gray mt-0.5">
                  {dateGuestsLabel}
                </div>
              </div>
            </button>
          </div>
        ) : (
          <div className="md:hidden px-6 py-4 flex items-center gap-3">
            <button
              onClick={activateMobileSearch}
              className="w-full flex items-center justify-center bg-white rounded-full shadow-md border border-gray-200 px-4 py-3"
              aria-label="Start your search"
              type="button"
            >
              <img
                src="/icons/search/search_text_icon.svg"
                alt=""
                className="mr-2 h-6 w-6"
              />
              <span className="text-sm text-ink-black font-medium">
                Start your search
              </span>
            </button>
          </div>
        )}
      </div>

      {/* Mobile Search Modals */}
      <AnimatePresence>
        {isMobile && isSearchActive && !isGuided && (
          <MobileSearchModal
            key="change-search"
            filters={filters}
            onClose={closeSearch}
            onSectionSelect={handleMobileSectionSelect}
            handleSearch={handleSearch}
          />
        )}
      </AnimatePresence>

      <AnimatePresence mode="wait">
        {isMobile && isSearchActive && !isGoingBack && (
          <>
            {mobileSearchSection === "where" && (
              <WhereModalMobile
                key="where-modal"
                filters={filters}
                setFilters={setFilters}
                onBack={isGuided ? leaveGuidedSearch : handleMobileBack}
                onSelected={isGuided ? () => goToStep("when") : undefined}
                onSearch={handleSearch}
              />
            )}
            {(mobileSearchSection === "when" ||
              (isGuided && mobileSearchSection === "who")) && (
              <BookingDateModalMobile
                key="when-modal"
                // The app's Select Dates page, as Message host and Book Now use.
                appearance="app"
                filters={filters}
                setFilters={setFilters}
                onBack={isGuided ? () => goToStep("where") : handleMobileBack}
                onDoneDates={(dates) => {
                  commitDates(dates);
                  if (isGuided) goToStep("who");
                  else handleMobileBack();
                }}
                doneLabel={isGuided ? "Next" : undefined}
              />
            )}
            {mobileSearchSection === "who" && (
              <BookingGuestModalMobile
                key="who-modal"
                appearance="app"
                filters={filters}
                updateGuestCount={updateGuestCount}
                onBack={isGuided ? () => goToStep("when") : handleMobileBack}
                onDoneGuests={(guests) => {
                  if (isGuided) {
                    finishGuidedSearch(guests);
                    return;
                  }
                  commitGuests(guests);
                  handleMobileBack();
                }}
                doneLabel="Next"
              />
            )}
          </>
        )}
      </AnimatePresence>
    </>
  );
};

export default React.memo(MobileHeader);
